import type {
  ICellRendererComp,
  ICellRendererParams,
  IRowNode,
} from "ag-grid-enterprise";

/**
 * AG Grid cell renderer that mounts a registered custom HTML element. The
 * element receives `value` and `row` via property assignment, so it must
 * expose setters for whichever it cares about.
 *
 * Wired in via `cellRenderer: CustomElementCellRenderer` and
 * `cellRendererParams: { tagName, attributes? }`.
 *
 * `attributes` is a string-to-string map applied once at init and not
 * re-applied on refresh — these come from the backend-driven column def
 * and do not churn mid-grid-lifetime.
 *
 * `value` is written from `init`/`refresh` when AG Grid considers values
 * different.
 *
 * `row` is written on every `dataChanged` event from the row
 * node, so cross-field row updates land regardless of value equality.
 */
export class CustomElementCellRenderer implements ICellRendererComp {
  private el!: HTMLElement & { value?: unknown; row?: unknown };
  private node!: IRowNode;
  private onRowDataChanged!: () => void;

  init(params: ICellRendererParams): void {
    const { tagName, attributes } = params as ICellRendererParams & {
      tagName: string;
      attributes?: Record<string, string>;
    };
    if (!customElements.get(tagName)) {
      console.warn(
        `[DATA GRID] cellRendererElement "${tagName}" is not a registered custom element. ` +
          `Import the file that calls customElements.define("${tagName}", …) before the grid mounts.`
      );
    }
    this.el = document.createElement(tagName);

    if (attributes) {
      for (const [name, value] of Object.entries(attributes)) {
        this.el.setAttribute(name, String(value));
      }
    }

    this.el.value = params.value;
    this.el.row = params.data;

    this.node = params.node;
    this.onRowDataChanged = () => {
      this.el.row = this.node.data;
    };
    this.node.addEventListener("dataChanged", this.onRowDataChanged);
  }

  getGui(): HTMLElement {
    return this.el;
  }

  refresh(params: ICellRendererParams): boolean {
    this.el.value = params.value;
    return true;
  }

  destroy(): void {
    this.node.removeEventListener("dataChanged", this.onRowDataChanged);
  }
}
